import { createRouter, createWebHashHistory } from 'vue-router';

import LandingPage from '@/components/paysol-landing/LandingPage.vue';
import Login from '@/components/paysol-auth/Login.vue';
import Register from '@/components/paysol-auth/Register.vue';
import Dashboard from '@/components/paysol-dashboard/Dashboard.vue';
import ProductManagement from '@/components/paysol-products/ProductManagement.vue';
import POSTerminal from '@/components/paysol-pos/POSTerminal.vue';
import StockManagement from '@/components/paysol-stock/StockManagement.vue';
import UserManagement from '@/components/paysol-users/UserManagement.vue';
import AuditLog from '@/components/paysol-audit/AuditLog.vue';
import SettingsPage from '@/components/paysol-settings/SettingsPage.vue';
import Reports from '@/components/paysol-reports/Reports.vue';
import CustomerManagement from '@/components/paysol-customers/CustomerManagement.vue';
import SupplierManagement from '@/components/paysol-suppliers/SupplierManagement.vue';
import PurchaseOrders from '@/components/paysol-purchase/PurchaseOrders.vue';
import StockTakes from '@/components/paysol-stock/StockTakes.vue';
import CatalogAdmin from '@/components/paysol-catalog/CatalogAdmin.vue';
import BarcodePrint from '@/components/paysol-products/BarcodePrint.vue';
import DiscountCodes from '@/components/paysol-catalog/DiscountCodes.vue';
import PriceRules from '@/components/paysol-catalog/PriceRules.vue';
import LaybyManagement from '@/components/paysol-laybys/LaybyManagement.vue';
import StockTransfers from '@/components/paysol-transfers/StockTransfers.vue';
import NotificationsLog from '@/components/paysol-notifications/NotificationsLog.vue';
import TablesFloor from '@/components/paysol-tables/TablesFloor.vue';
import WaiterPOS from '@/components/paysol-tables/WaiterPOS.vue';
import TabsPage from '@/components/paysol-tables/TabsPage.vue';
import KitchenDisplay from '@/components/paysol-kitchen/KitchenDisplay.vue';
import AppointmentsCalendar from '@/components/paysol-appointments/AppointmentsCalendar.vue';
import PublicBookingPage from '@/components/paysol-public/PublicBookingPage.vue';
import PublicReceiptPage from '@/components/paysol-receipt/PublicReceiptPage.vue';
import PlatformAdmin from '@/components/paysol-admin/PlatformAdmin.vue';

const routes = [
  { path: '/',         name: 'landing',  component: LandingPage, meta: { public: true } },
  { path: '/book/:slug', name: 'public-booking', component: PublicBookingPage, meta: { public: true } },
  { path: '/r/:receiptNo', name: 'public-receipt', component: PublicReceiptPage, meta: { public: true } },
  { path: '/login',    name: 'login',    component: Login,       meta: { public: true } },
  { path: '/register', name: 'register', component: Register,    meta: { public: true } },

  { path: '/dashboard', name: 'dashboard', component: Dashboard,          meta: { actionKey: null } },
  { path: '/products',  name: 'products',  component: ProductManagement,  meta: { actionKey: 'products.view' } },
  { path: '/catalog',   name: 'catalog',   component: CatalogAdmin,       meta: { actionKey: 'products.view' } },
  { path: '/barcodes',  name: 'barcodes',  component: BarcodePrint,       meta: { actionKey: 'products.view' } },
  { path: '/discounts', name: 'discounts', component: DiscountCodes,      meta: { actionKey: 'products.view' } },
  { path: '/price-rules', name: 'price-rules', component: PriceRules,     meta: { actionKey: 'products.view' } },
  { path: '/pos',       name: 'pos',       component: POSTerminal,        meta: { actionKey: 'sales.create' } },
  { path: '/stock',     name: 'stock',     component: StockManagement,    meta: { actionKey: 'stock.view' } },
  { path: '/stock-takes', name: 'stock-takes', component: StockTakes,     meta: { actionKey: 'stock.take' } },
  { path: '/stock-transfers', name: 'stock-transfers', component: StockTransfers, meta: { actionKey: 'stock.transfer' } },
  { path: '/suppliers', name: 'suppliers', component: SupplierManagement, meta: { actionKey: 'suppliers.view' } },
  { path: '/purchase-orders', name: 'purchase-orders', component: PurchaseOrders, meta: { actionKey: 'po.view' } },
  { path: '/laybys',   name: 'laybys',   component: LaybyManagement,   meta: { actionKey: 'laybys.view' } },
  { path: '/notifications', name: 'notifications', component: NotificationsLog, meta: { actionKey: null } },

  // Restaurant mode
  { path: '/tables',          name: 'tables',   component: TablesFloor,     meta: { actionKey: 'sales.create' } },
  { path: '/tabs',            name: 'tabs',     component: TabsPage,        meta: { actionKey: 'sales.create' } },
  { path: '/waiter/:orderId', name: 'waiter',   component: WaiterPOS,       meta: { actionKey: 'sales.create' } },
  { path: '/kitchen',         name: 'kitchen',  component: KitchenDisplay,  meta: { actionKey: null } },

  // Service / salon mode
  { path: '/appointments',    name: 'appointments', component: AppointmentsCalendar, meta: { actionKey: 'sales.create' } },

  // Cross-tenant platform ownership
  { path: '/admin',           name: 'platform-admin', component: PlatformAdmin, meta: { requirePlatformOwner: true } },
  { path: '/customers', name: 'customers', component: CustomerManagement, meta: { actionKey: 'customers.view' } },
  { path: '/users',     name: 'users',     component: UserManagement,     meta: { actionKey: 'users.view' } },
  { path: '/reports',   name: 'reports',   component: Reports,            meta: { actionKey: 'reports.view' } },
  { path: '/audit',     name: 'audit',     component: AuditLog,           meta: { actionKey: 'audit.view' } },
  { path: '/settings',  name: 'settings',  component: SettingsPage,       meta: { actionKey: null } },

  { path: '/:pathMatch(.*)*', redirect: '/' }
];

const router = createRouter({ history: createWebHashHistory(), routes });

// 10-min inactivity timeout
let userActivityTimer;
function resetInactivity() {
  if (!sessionStorage.getItem('isLoggedIn')) return;
  clearTimeout(userActivityTimer);
  userActivityTimer = setTimeout(() => {
    sessionStorage.removeItem('isLoggedIn');
    router.push('/login');
  }, 10 * 60 * 1000);
}

router.beforeEach((to, from, next) => {
  if (to.meta && to.meta.public) return next();
  if (!sessionStorage.getItem('isLoggedIn')) return next('/login');

  // platform-owner gate
  if (to.meta && to.meta.requirePlatformOwner) {
    if (sessionStorage.getItem('isPlatformOwner') !== '1') return next('/dashboard');
  }

  // action-key gate
  if (to.meta && to.meta.actionKey) {
    const role = sessionStorage.getItem('userRole');
    if (role !== 'owner') {
      let allowed = [];
      try { allowed = JSON.parse(sessionStorage.getItem('allowedActions') || '[]'); } catch (e) {}
      if (!allowed.includes('*') && !allowed.includes(to.meta.actionKey)) {
        return next('/dashboard');
      }
    }
  }
  resetInactivity();
  next();
});

if (typeof window !== 'undefined') {
  ['mousemove', 'keypress', 'touchstart'].forEach(ev => document.addEventListener(ev, resetInactivity));
}

export default router;
